import type { VisitRecord } from './index'
import getTodayInUserTimezone from '../../../../utils/getTodayInUserTimezone'
import { getUserTimezone } from '../../../../utils/GetUserTimezone'

const getBaseTimeForVisit = (record: VisitRecord): string | null => {
  if (record.visit?.serviceEndTime) return record.visit.serviceEndTime
  if (record.visit?.appointmentTime) return record.visit.appointmentTime
  if (record.checkinTime) return record.checkinTime
  return null
}

const isOnTodayInUserTimezone = (timestamp: string | null, today: string, timezone: string): boolean => {
  if (!timestamp) return false

  const date = new Date(timestamp)
  if (Number.isNaN(date.getTime())) return false

  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })

  const dateInUserTz = formatter.format(date)
  return dateInUserTz === today
}

export const processQueue = (data: VisitRecord[]) => {
  const today = getTodayInUserTimezone()
  const timezone = getUserTimezone()

  const filtered = data.filter((item) => isOnTodayInUserTimezone(getBaseTimeForVisit(item), today, timezone))

  const checkedInList: VisitRecord[] = []
  const upcomingList: VisitRecord[] = []
  const previousList: VisitRecord[] = []

  // --- Bucket split ---
  filtered.forEach((item) => {
    const status = item?.queueStatus || ''
    if (status === 'waiting' || status === 'WAITING') checkedInList.push(item)
    else if (status === 'upcoming' || status === 'UPCOMING') upcomingList.push(item)
    else if (status === 'completed' || status === 'COMPLETED') previousList.push(item)
  })

  // --- Sorting logic ---
  const threshold = 10 * 60 * 1000

  // Checked-in sort
  checkedInList.sort((a, b) => {
    const checkA: any = a.checkinTime ? new Date(a.checkinTime).getTime() : null
    const checkB: any = b.checkinTime ? new Date(b.checkinTime).getTime() : null
    const apptA: any = a.visit?.appointmentTime ? new Date(a.visit.appointmentTime).getTime() : null
    const apptB: any = b.visit?.appointmentTime ? new Date(b.visit.appointmentTime).getTime() : null

    const isLateA: any = checkA > apptA + threshold
    const isLateB: any = checkB > apptB + threshold

    if (isLateA !== isLateB) return Number(isLateA) - Number(isLateB)
    if (isLateA) return checkA - checkB
    if (apptA !== apptB) return apptA - apptB
    return checkA - checkB
  })

  // Upcoming sort
  upcomingList.sort((a: any, b: any) => {
    const apptA: any = a.visit?.appointmentTime ? new Date(a.visit.appointmentTime).getTime() : null
    const apptB: any = b.visit?.appointmentTime ? new Date(b.visit.appointmentTime).getTime() : null
    return apptA - apptB
  })

  // Previous sort
  previousList.sort((a, b) => {
    const endA = a.visit?.serviceEndTime ? new Date(a.visit.serviceEndTime).getTime() : 0
    const endB = b.visit?.serviceEndTime ? new Date(b.visit.serviceEndTime).getTime() : 0
    return endB - endA
  })

  return {
    checkedIn: checkedInList,
    upcoming: upcomingList,
    previous: previousList,
  }
}
